import React, {useState, useEffect, useRef, useContext} from 'react';
import {Button, Icon} from '@blueprintjs/core';
import {uniq} from 'lodash';

import {areaContext} from '/src/services/api';
import {errorToast} from '/src/utils/toaster';
import {Table, Row, Cell} from '/src/components/tables/table'
import {DataPageContext} from '/src/pages/data/context';

import EditingItemForm from './editing-item-form';
import ItemRow from './item-row';


export default function ShapesContexts({
    recording,
    elems,
    taxonomiesInUse: propTaxonomiesInUse,
    loadingTaxonomies,
    updateEditRecordingPageState,
    setShapeHighlight,
}) {
    const {loading, setLoading} = useContext(DataPageContext);

    const [areaContexts, setAreaContexts] = useState(recording?.areaContexts);
    const [currentTaxonomies, setCurrentTaxonomies] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [newItem, setNewItem] = useState(null);
    const [taxonomiesInUse, setTaxonomiesInUse] = useState(propTaxonomiesInUse);

    const needsSyncRef = useRef(false);

    // Initialise from props if state is null/undefined
    useEffect(() => {
        if (!areaContexts && recording?.areaContexts?.length)
            setAreaContexts(recording.areaContexts);
    }, [recording?.areaContexts]);

    useEffect(() => {
        if (!taxonomiesInUse && propTaxonomiesInUse?.length)
            setTaxonomiesInUse(propTaxonomiesInUse);
    }, [propTaxonomiesInUse]);

    // Sync to recording + trigger save after state updates flagged by needsSyncRef
    useEffect(() => {
        if (needsSyncRef.current) {
            needsSyncRef.current = false;
            recording.areaContexts = areaContexts;
            updateEditRecordingPageState();
        }
    }, [areaContexts]);

    function beginEditingContext(contextBeingEdited) {
        if (editingItem) {
            const continueEdit = window.confirm('Cancel editing the other item?');
            if (continueEdit) setEditingItem(contextBeingEdited);
        } else {
            setEditingItem(contextBeingEdited);
        }
    }

    function stopEditingContext() {
        setEditingItem(null);
    }

    function beginAddingNewContext() {
        setNewItem({type: 'taxonomy'});
    }

    function editNewContext(contextBeingAdded) {
        setNewItem(contextBeingAdded);
    }

    function stopAddingNewContext() {
        setNewItem(null);
    }

    function validateContext(context) {
        let doThrow = false;
        context.taxonomy = context.type === "taxonomy" ? context.taxonomy : undefined;
        if (!context.type) {
            errorToast({message: 'Missing a type'});
            doThrow = true;
        }
        if (!context.areaId) {
            errorToast({message: 'Missing an areaId'});
            doThrow = true;
        }
        if (context.type === 'taxonomy' && !context.taxonomy) {
            errorToast({message: 'Missing a taxonomy'});
            doThrow = true;
        }
        if (doThrow) throw new Error('Invalid form');
    }

    async function saveNewContext(context) {
        const prevLocation = loading.location;
        setLoading({status: true, location: "Saving area context..."});
        try {
            validateContext(context);
            const response = await areaContext.create(
                context,
                recording.id
            );

            if (response && response.id) {
                const newContext = {
                    ...context,
                    id: response.id
                };
                needsSyncRef.current = true;
                setNewItem(null);
                setAreaContexts([...(areaContexts || []), newContext]);
                setTaxonomiesInUse(uniq([
                    ...taxonomiesInUse,
                    context.taxonomy
                ]));
            } else {
                errorToast({message: 'Your area context wasn\'t saved'});
            }
        } catch (e) {
            // validation or API error
        } finally {
            setLoading({status: false, location: prevLocation});
        }
    }

    async function saveExistingContext(context) {
        const prevLocation = loading.location;
        setLoading({status: true, location: "Saving area context..."});
        try {
            validateContext(context);
            await areaContext.update(context, recording.id);
            needsSyncRef.current = true;
            setEditingItem(null);
            setAreaContexts([
                ...areaContexts.filter(ac => ac.id !== context.id),
                context
            ]);
            setTaxonomiesInUse(uniq([
                ...taxonomiesInUse,
                context.taxonomy
            ]));
        } catch (e) {
            // validation or API error
        } finally {
            setLoading({status: false, location: prevLocation});
        }
    }

    async function deleteContext(context) {
        const continueDelete = window.confirm(
            'Are you sure you want to delete this areaContext?'
        );
        if (continueDelete) {
            const prevLocation = loading.location;
            setLoading({status: true, location: "Deleting area context..."});
            try {
                await areaContext.delete(context.id);
                needsSyncRef.current = true;
                setAreaContexts([
                    ...areaContexts.filter(ac => ac.id !== context.id)
                ]);
            } catch (e) {
                errorToast({message: 'Failed to delete area context'});
            } finally {
                setLoading({status: false, location: prevLocation});
            }
        }
    }

    return (
        <Table>
            <Row>
                <Cell haligh="left" valign="center" span={2}>
                    {areaContexts?.length ?? "No"} area contexts
                </Cell>
                <Cell span={1}>
                    <Button
                        icon="plus"
                        text="Add New"
                        fill={true}
                        onClick={beginAddingNewContext}
                    />
                </Cell>
            </Row>
            <Row $separatorColor="silver">
                <Cell header span={2} halign="center">Type</Cell>
                <Cell header span={3} halign="center">Area</Cell>
                <Cell header span={3} halign="center">Taxonomy</Cell>
                <Cell header span={1} halign="center"><Icon icon="more"/></Cell>
            </Row>
            {areaContexts &&
                areaContexts
                    .sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))
                    .map(ac => {
                        if (editingItem && editingItem.id === ac.id)
                            return <EditingItemForm
                                key={ac.id}
                                item={editingItem}
                                editItem={setEditingItem}
                                cancelEditing={stopEditingContext}
                                elems={elems}
                                taxonomiesInUse={taxonomiesInUse}
                                setShapeHighlight={setShapeHighlight}
                                saveItem={saveExistingContext}
                                loadingTaxonomies={loadingTaxonomies}
                            />
                        else
                            return <ItemRow
                                key={`${ac.areaId}${ac.taxonomy}_${ac.id}`}
                                area={elems.areas.find(area => area.name === ac.areaId)}
                                areaContext={ac}
                                beginEditingItem={beginEditingContext}
                                deleteContext={deleteContext}
                                setShapeHighlight={setShapeHighlight}
                            />
                    })}
            {newItem && (
                <Row><Cell halign="center"><h4>Adding new item</h4></Cell></Row>
            )}
            {newItem && (
                <EditingItemForm
                    item={newItem}
                    editItem={editNewContext}
                    cancelEditing={stopAddingNewContext}
                    elems={elems}
                    taxonomiesInUse={taxonomiesInUse}
                    setShapeHighlight={setShapeHighlight}
                    saveItem={saveNewContext}
                />
            )}
        </Table>
    );
}
